import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Form, Alert } from 'react-bootstrap';
import { 
  BuildingStorefrontIcon,
  ShoppingCartIcon,
  ShoppingBagIcon,
  CheckCircleIcon,
  XMarkIcon,
  SparklesIcon
} from '@heroicons/react/24/solid';
import { useAuth } from '../../contexts/AuthContext';
import { productAPI, cartAPI } from '../../services/api';
import { Product } from '../../types';
import Header from '../common/Header';
import Footer from '../common/Footer';

const ProductCatalog: React.FC = () => {
  const { user, isExpired } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState<{[key: number]: string}>({});
  const [addingToCart, setAddingToCart] = useState<{[key: number]: boolean}>({});
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await productAPI.getProducts();
      console.log('Products API response:', response.data);
      // Handle both direct array and paginated response
      const productData = Array.isArray(response.data) 
        ? response.data 
        : (response.data as any).results || [];
      setProducts(productData);
    } catch (error) {
      console.error('Error fetching products:', error);
      // Set fallback data on error
      setProducts([
        {
          id: 1,
          name: 'Artisan Sourdough Bread',
          sku: 'SB001',
          category: { id: 1, name: 'Bread', description: 'Fresh baked breads' },
          description: 'Handcrafted sourdough bread with crispy crust',
          unit: 'loaf',
          price_per_unit: 4.50,
          minimum_order_quantity: 5,
          stock_quantity: 50,
          is_available: true
        },
        {
          id: 2,
          name: 'Chocolate Croissants',
          sku: 'CR002',
          category: { id: 2, name: 'Pastries', description: 'Delicious pastries' },
          description: 'Buttery croissants filled with premium chocolate',
          unit: 'piece',
          price_per_unit: 3.25,
          minimum_order_quantity: 10,
          stock_quantity: 75,
          is_available: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (productId: number, value: string) => {
    setQuantities(prev => ({ ...prev, [productId]: value }));
  };

  const handleQuantityBlur = (product: Product) => {
    const raw = quantities[product.id];
    const parsed = parseInt(raw);
    if (!raw || isNaN(parsed) || parsed < product.minimum_order_quantity) {
      setQuantities(prev => ({ ...prev, [product.id]: String(product.minimum_order_quantity) }));
    } else if (parsed > product.stock_quantity) {
      setQuantities(prev => ({ ...prev, [product.id]: String(product.stock_quantity) }));
    } else {
      setQuantities(prev => ({ ...prev, [product.id]: String(parsed) }));
    }
  };

  const handleAddToCart = async (product: Product) => {
    const raw = quantities[product.id];
    const quantity = parseInt(raw) || product.minimum_order_quantity;
    
    if (quantity < product.minimum_order_quantity) {
      setErrorMessage(`Minimum order quantity is ${product.minimum_order_quantity} ${product.unit}s`);
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    setAddingToCart(prev => ({ ...prev, [product.id]: true }));

    try {
      await cartAPI.addToCart(product.id, quantity);
      setSuccessMessage(`Added ${quantity} ${product.unit}${quantity > 1 ? 's' : ''} of ${product.name} to cart!`);
      setTimeout(() => setSuccessMessage(''), 3000);
      // Reset quantity to minimum
      setQuantities(prev => ({ ...prev, [product.id]: String(product.minimum_order_quantity) }));
    } catch (error) {
      console.error('Error adding to cart:', error);
      setErrorMessage('Failed to add item to cart. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setAddingToCart(prev => ({ ...prev, [product.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100 app-background">
      <Header />
      <div className="flex-grow-1">
        <Container className="py-3 py-md-5 page-content-padding">
          {/* Page Header */}
          <div className="text-center mb-3 mb-md-5">
            <h1 className="display-4 fw-bold text-primary mb-2 d-flex align-items-center justify-content-center gap-2">
              <ShoppingBagIcon className="page-header-icon" style={{ width: '40px', height: '40px' }} />
              Product Catalog
            </h1>
            <p className="lead text-muted mb-0">Discover our fresh bakery products</p>
          </div>

          {successMessage && (
            <Alert variant="success" className="mb-4">
              <strong>Success!</strong> {successMessage}
            </Alert>
          )}

          {errorMessage && (
            <Alert variant="danger" className="mb-4" dismissible onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}

          <Row>
            {products.map((product) => {
            // Calculate discounted price if user has a discount
            const originalPrice = typeof product.price_per_unit === 'string' 
              ? parseFloat(product.price_per_unit) 
              : product.price_per_unit;
            const discountPercent = user?.discount_percentage || 0;
            const discountedPrice = discountPercent > 0 
              ? originalPrice * (1 - discountPercent / 100) 
              : originalPrice;
            const hasDiscount = discountPercent > 0;
            const currentQuantity = quantities[product.id] ?? String(product.minimum_order_quantity);
            const currentQuantityNum = parseInt(currentQuantity) || 0;
            const isAddingToCart = addingToCart[product.id] || false;

            return (
            <Col xs={12} sm={6} lg={4} key={product.id} className="mb-4">
              <Card className="professional-card h-100 product-card">
                <div className="card-image-placeholder">
                  <div className="placeholder-icon d-flex justify-content-center align-items-center">
                    <BuildingStorefrontIcon style={{ width: '48px', height: '48px' }} className="text-muted" />
                  </div>
                </div>
                <Card.Body className="d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h5 className="card-title fw-bold mb-0">{product.name}</h5>
                    <Badge 
                      bg={product.is_available ? 'success' : 'danger'} 
                      className="rounded-pill"
                    >
                      {product.is_available ? (
                        <span className="d-flex align-items-center gap-1">
                          <CheckCircleIcon style={{ width: '16px', height: '16px' }} />
                          Available
                        </span>
                      ) : (
                        <span className="d-flex align-items-center gap-1">
                          <XMarkIcon style={{ width: '16px', height: '16px' }} />
                          Out of Stock
                        </span>
                      )}
                    </Badge>
                  </div>
                  
                  <p className="text-muted small mb-2">SKU: {product.sku}</p>
                  <p className="card-text text-muted mb-3">{product.description}</p>
                  
                  <div className="product-details mb-3">
                    <div className="detail-item">
                      <span className="detail-label">Price:</span>
                      <div className="d-flex flex-column align-items-end flex-grow-1">
                        {hasDiscount ? (
                          <>
                            <span className="detail-value text-primary fw-bold">
                              ${discountedPrice.toFixed(2)} per {product.unit}
                            </span>
                            <span className="text-muted text-decoration-line-through small">
                              ${originalPrice.toFixed(2)} per {product.unit}
                            </span>
                            <Badge bg="success" className="mt-1 rounded-pill small">
                              <SparklesIcon style={{ width: '16px', height: '16px' }} className="me-1" />
                              {discountPercent}% OFF
                            </Badge>
                          </>
                        ) : (
                          <span className="detail-value text-primary fw-bold">
                            ${originalPrice.toFixed(2)} per {product.unit}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Min. Order:</span>
                      <span className="detail-value">
                        {product.minimum_order_quantity} {product.unit}s
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Stock:</span>
                      <span className="detail-value">
                        <Badge bg="light" text="dark" className="rounded-pill">
                          {product.stock_quantity} available
                        </Badge>
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-auto">
                    {product.is_available ? (
                      <div className="add-to-cart-section">
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <Form.Label className="mb-0 small fw-bold">Quantity:</Form.Label>
                          <Form.Control
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={currentQuantity}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || /^\d+$/.test(val)) {
                                handleQuantityChange(product.id, val);
                              }
                            }}
                            onBlur={() => handleQuantityBlur(product)}
                            size="sm"
                            style={{ width: '80px' }}
                          />
                          <span className="small text-muted">{product.unit}s</span>
                        </div>
                        <Button 
                          variant="primary"
                          className="btn-gradient w-100"
                          disabled={isAddingToCart || currentQuantityNum < product.minimum_order_quantity || isExpired}
                          onClick={() => handleAddToCart(product)}
                          title={isExpired ? 'Read-only mode — upgrade to place orders' : undefined}
                        >
                          {isAddingToCart ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Adding...
                            </>
                          ) : (
                            <>
                              <ShoppingCartIcon style={{ width: '16px', height: '16px' }} className="me-2" />
                              Add to Cart
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        variant="secondary"
                        className="w-100"
                        disabled
                      >
                        Out of Stock
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            );
          })}
        </Row>

        {products.length === 0 && (
          <div className="text-center py-5">
            <div className="empty-state">
              <h3 className="text-muted mb-3">No products available</h3>
              <p className="text-muted">Please check back later for new products.</p>
            </div>
          </div>
        )}
        </Container>
      </div>
      <Footer />
    </div>
  );
};

export default ProductCatalog;